/**
 * web-client-app D9 / spec "Options cannot break a piece": options are checked
 * at mount against the piece's contract. An unknown option, or a value outside
 * its allowed list or range, is ignored with a console warning and the default
 * stands. No option may hide a part the contract marks required.
 */
import type { PieceContract, ResolvedOptions } from './define';

export type OptionWarn = (message: string) => void;

const defaultWarn: OptionWarn = (message) => console.warn(message);

export function resolveOptions<C extends PieceContract>(
  contract: C,
  raw: Readonly<Record<string, unknown>> | undefined,
  warn: OptionWarn = defaultWarn,
): ResolvedOptions<C> {
  const out: Record<string, unknown> = {};
  for (const [name, spec] of Object.entries(contract.options)) out[name] = spec.default;
  if (!raw) return out as ResolvedOptions<C>;

  for (const [name, value] of Object.entries(raw)) {
    if (value === undefined) continue;
    const spec = contract.options[name];
    const refuse = (why: string) =>
      warn(`[telify] ${contract.name}: option "${name}" ${why}; using ${JSON.stringify(spec?.default ?? null)}.`);
    if (!spec) {
      warn(`[telify] ${contract.name}: unknown option "${name}" ignored.`);
      continue;
    }
    switch (spec.type) {
      case 'boolean':
        if (typeof value !== 'boolean') {
          refuse('must be true or false');
          break;
        }
        if (value === false && spec.showsPart && contract.parts[spec.showsPart]?.required) {
          refuse(`cannot hide the required part "${spec.showsPart}"`);
          break;
        }
        out[name] = value;
        break;
      case 'enum':
        if (typeof value !== 'string' || !spec.values.includes(value)) {
          refuse(`must be one of ${spec.values.join(', ')}`);
          break;
        }
        out[name] = value;
        break;
      case 'number':
        if (typeof value !== 'number' || !Number.isFinite(value) || value < spec.min || value > spec.max) {
          refuse(`must be a number from ${spec.min} to ${spec.max}`);
          break;
        }
        out[name] = value;
        break;
      case 'string':
        if (value !== null && (typeof value !== 'string' || value.length > spec.maxLength)) {
          refuse(`must be text of at most ${spec.maxLength} characters`);
          break;
        }
        out[name] = value;
        break;
    }
  }
  return out as ResolvedOptions<C>;
}
